'use client';

import { useMemo, useState } from 'react';
import { FileText, Menu, Search, X } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import microthemesData from '@/components/data/microthemes-pyq.json';
import { pyqEntries } from '@/lib/pyq';

const navItems = [
  { href: '/microthemes-pyq', label: 'PYQ Microthemes' },
  { href: '/syllabus', label: 'UPSC Syllabus' },
  { href: '/mains-test-series-comparison', label: 'Mains Test Series' }
];

const paperLabels = {
  All: 'All GS Papers',
  1: 'GS Paper 1',
  2: 'GS Paper 2',
  3: 'GS Paper 3',
  4: 'GS Paper 4'
};

function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur">
      <nav className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        <Link href="/" className="flex items-center gap-3">
          <Image src="/logo.jpg" alt="UPSC IAS Syllabus Logo" width={36} height={36} className="h-9 w-9" priority />
          <span className="text-lg font-semibold text-slate-950">UPSC IAS Syllabus</span>
        </Link>

        <div className="hidden items-center gap-6 md:flex">
          {navItems.map((item) => (
            <Link key={item.href} href={item.href} className="text-sm font-medium text-slate-700 hover:text-sky-700">
              {item.label}
            </Link>
          ))}
        </div>

        <button
          className="md:hidden"
          onClick={() => setMobileMenuOpen((open) => !open)}
          aria-label="Toggle navigation"
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {mobileMenuOpen && (
        <div className="border-t border-slate-200 bg-white px-4 py-3 md:hidden">
          {navItems.map((item) => (
            <Link key={item.href} href={item.href} className="block rounded px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}

export default function MicroThemesPYQ() {
  const [activeGS, setActiveGS] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [yearFilter, setYearFilter] = useState('All');
  const [microthemeFilter, setMicrothemeFilter] = useState('All');

  const years = useMemo(() => {
    const uniqueYears = Array.from(new Set(pyqEntries.map((item) => item.Year)));
    return ['All', ...uniqueYears.sort((a, b) => Number(b) - Number(a))];
  }, []);

  const microthemes = useMemo(() => {
    const uniqueMicrothemes = Array.from(new Set(pyqEntries.map((item) => item.Microtheme)));
    return ['All', ...uniqueMicrothemes.sort((a, b) => a.localeCompare(b))];
  }, []);

  const paperCounts = useMemo(() => {
    return pyqEntries.reduce(
      (counts, item) => {
        counts.All += 1;
        counts[item.GS] = (counts[item.GS] || 0) + 1;
        return counts;
      },
      { All: 0 }
    );
  }, []);

  const filteredData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return pyqEntries.filter((item) => {
      const matchesGS = activeGS === 'All' || item.GS === activeGS;
      const matchesYear = yearFilter === 'All' || item.Year === yearFilter;
      const matchesMicrotheme = microthemeFilter === 'All' || item.Microtheme === microthemeFilter;
      const matchesSearch =
        !query ||
        item.Microtheme.toLowerCase().includes(query) ||
        item.PYQ.toLowerCase().includes(query);

      return matchesGS && matchesYear && matchesMicrotheme && matchesSearch;
    });
  }, [activeGS, searchQuery, yearFilter, microthemeFilter]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <Header />

      <main>
        <section className="border-b border-slate-200 bg-gradient-to-br from-white via-sky-50 to-emerald-50 px-4 py-12 sm:px-6">
          <div className="mx-auto max-w-6xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-sky-700">UPSC Mains PYQ Microthemes</p>
            <h1 className="max-w-4xl text-4xl font-bold tracking-tight text-slate-950 md:text-5xl">
              UPSC Mains previous year questions by microtheme
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-700">
              Search UPSC Mains GS previous year questions by paper, year, topic and microtheme. The table includes {microthemesData.length} questions from 2010 to 2024.
            </p>
          </div>
        </section>

        <section className="px-4 py-10 sm:px-6">
          <div className="mx-auto max-w-6xl">
            <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {Object.entries(paperLabels).map(([paper, label]) => (
                <button
                  key={paper}
                  onClick={() => setActiveGS(paper)}
                  className={`rounded-md border px-4 py-3 text-left transition ${
                    activeGS === paper
                      ? 'border-sky-700 bg-sky-700 text-white'
                      : 'border-slate-200 bg-white text-slate-800 hover:border-sky-300'
                  }`}
                >
                  <span className="block text-sm font-semibold">{label}</span>
                  <span className={`mt-1 block text-xs ${activeGS === paper ? 'text-sky-100' : 'text-slate-500'}`}>
                    {paperCounts[paper] || 0} questions
                  </span>
                </button>
              ))}
            </div>

            <div className="mb-6 grid gap-4 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-2 lg:grid-cols-[minmax(280px,1fr)_280px_180px]">
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-700">Search question or microtheme</span>
                <span className="relative block">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Example: federalism, women, climate, integrity"
                    className="w-full rounded-md border border-slate-300 py-3 pl-10 pr-4 text-sm outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100"
                  />
                </span>
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-700">Microtheme</span>
                <select
                  value={microthemeFilter}
                  onChange={(event) => setMicrothemeFilter(event.target.value)}
                  className="w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100"
                >
                  {microthemes.map((microtheme) => (
                    <option key={microtheme} value={microtheme}>
                      {microtheme === 'All' ? 'All microthemes' : microtheme}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-700">Year</span>
                <select
                  value={yearFilter}
                  onChange={(event) => setYearFilter(event.target.value)}
                  className="w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year === 'All' ? 'All years' : year}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="mb-4 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
              <h2 className="text-2xl font-bold text-slate-950">
                UPSC Mains PYQ list
              </h2>
              <p className="text-sm font-medium text-slate-600">
                Showing {filteredData.length} of {microthemesData.length} questions
              </p>
            </div>

            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] border-collapse">
                  <thead className="bg-slate-950 text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">GS Paper</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Microtheme</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Question</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide">Year</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide">Marks</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {filteredData.map((item) => (
                      <tr key={item.slug} className="hover:bg-sky-50/60">
                        <td className="whitespace-nowrap px-4 py-4 text-sm font-semibold text-sky-800">GS {item.GS}</td>
                        <td className="px-4 py-4 text-sm font-medium text-slate-900">{item.Microtheme}</td>
                        <td className="px-4 py-4 text-sm leading-6 text-slate-700">
                          <Link href={`/pyq/${item.slug}`} className="hover:text-sky-800 hover:underline hover:underline-offset-2">
                            {item.PYQ}
                          </Link>
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-center text-sm text-slate-700">{item.Year}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-center text-sm text-slate-700">{item.Marks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredData.length === 0 && (
                <div className="flex flex-col items-center px-4 py-14 text-center">
                  <FileText className="h-10 w-10 text-slate-400" />
                  <p className="mt-3 text-lg font-semibold text-slate-900">No questions found</p>
                  <button
                    onClick={() => {
                      setActiveGS('All');
                      setSearchQuery('');
                      setYearFilter('All');
                      setMicrothemeFilter('All');
                    }}
                    className="mt-4 rounded-md bg-sky-700 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-800"
                  >
                    Reset filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-gray-950 px-4 py-12 text-gray-400">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 grid grid-cols-1 gap-8 md:grid-cols-3">
            <div>
              <div className="mb-4 flex items-center space-x-3">
                <Image src="/logo-bg-removed.png" alt="UPSC IAS Syllabus Logo" width={42} height={42} className="h-10 w-10" />
                <span className="font-bold text-white">UPSC IAS Syllabus</span>
              </div>
              <p className="text-sm">UPSC syllabus, microthemes and previous year questions.</p>
            </div>

            <div>
              <h4 className="mb-4 font-semibold text-white">Quick Links</h4>
              <div className="space-y-2 text-sm">
                <Link href="/microthemes-pyq" className="block transition hover:text-white">PYQ Microthemes</Link>
                <Link href="/syllabus" className="block transition hover:text-white">Syllabus</Link>
              </div>
            </div>

            <div>
              <h4 className="mb-4 font-semibold text-white">Mains</h4>
              <div className="space-y-2 text-sm">
                <Link href="/mains-test-series-comparison" className="block transition hover:text-white">Mains Test Series</Link>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>&copy; 2025 UPSC IAS Syllabus. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
